import { Film, Cog, Server, Atom, Database, Sparkles, MonitorPlay, RefreshCw, Plug } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const stack = [
  {
    id: "core",
    label: "Core",
    caption: "Studio-grade content ops",
    color: "#F2A900",
    modules: [
      { icon: Film, name: "Content Sourcing", desc: "Studios, OTT and regional distributors" },
      { icon: Cog, name: "AeroLab", desc: "Encoding, QC and metadata (TPN Gold)" },
      { icon: Server, name: "Delivery", desc: "IFE systems and AeroHub" },
    ],
  },
  {
    id: "nucleus",
    label: "NucleusOS",
    caption: "The intelligence layer",
    color: "#3B82F6",
    modules: [
      { icon: Atom, name: "Discovery", desc: "AI curation across catalogues" },
      { icon: Database, name: "Data", desc: "Route, cabin, passenger telemetry" },
      { icon: Sparkles, name: "Recommendation (DARE)", desc: "Real-time preference learning" },
    ],
  },
  {
    id: "experience",
    label: "Experience",
    caption: "The passenger surface",
    color: "#A855F7",
    modules: [
      { icon: MonitorPlay, name: "IFE Minisite & OTT", desc: "Branded surface, seat-back and BYOD" },
      { icon: RefreshCw, name: "Feedback loop", desc: "Every flight teaches the system" },
    ],
  },
];

// Hex helper — with-alpha shading for the layer's tinted header
const withAlpha = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const SlideStack = () => {
  const { isDark } = useTheme();
  return (
    <div>
      <div className="k-eyebrow" style={{ color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)" }}>
        Our platform
      </div>
      <h2 className={`k-h2 mt-3 max-w-[16ch] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
        One system.
        <br />
        <span style={{ color: "var(--k-accent)" }}>Three layers.</span>
      </h2>
      <p className={`k-lead mt-6 max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
        Content operations, NucleusOS intelligence and the passenger experience —
        connected end to end so each layer makes the next smarter.
      </p>

      <div className="mt-10 flex items-stretch gap-0">
        {stack.map((layer, idx) => (
          <div key={layer.id} className="flex items-stretch flex-1 min-w-0">
            <div
              className={`flex-1 min-w-0 rounded-2xl border overflow-hidden flex flex-col ${
                isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10"
              }`}
            >
              {/* Colored header */}
              <div
                className={`px-5 pt-4 pb-4 border-b ${isDark ? "border-white/10" : "border-black/10"}`}
                style={{ backgroundColor: withAlpha(layer.color, isDark ? 0.14 : 0.10) }}
              >
                <div className="k-eyebrow" style={{ color: layer.color }}>{layer.label}</div>
                <div
                  className="mt-1 text-[16px] font-semibold tracking-tight"
                  style={{ color: layer.color }}
                >
                  {layer.caption}
                </div>
              </div>

              <ul className={`divide-y ${isDark ? "divide-white/10" : "divide-black/10"} flex-1`}>
                {layer.modules.map((m) => {
                  const Icon = m.icon;
                  return (
                    <li key={m.name} className="px-5 py-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: layer.color }} />
                        <span className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{m.name}</span>
                      </div>
                      <div className={`text-[12px] leading-snug ${isDark ? "text-white/55" : "text-black/55"}`}>{m.desc}</div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Connector between layers (not after the last one) */}
            {idx < stack.length - 1 && (
              <div className="flex items-center justify-center px-2 flex-shrink-0" aria-hidden="true">
                <div className="flex items-center">
                  <span
                    className="w-4 h-[2px]"
                    style={{ backgroundColor: layer.color }}
                  />
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center border-2"
                    style={{
                      borderColor: withAlpha(layer.color, 0.5),
                      background: isDark ? "#0b0b0d" : "#fbfbf9",
                    }}
                  >
                    <Plug
                      className="w-3.5 h-3.5"
                      style={{ color: layer.color, transform: "rotate(-45deg)" }}
                    />
                  </span>
                  <span
                    className="w-4 h-[2px]"
                    style={{ backgroundColor: stack[idx + 1].color }}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SlideStack;
